import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { usePlatform } from '../context/PlatformContext';
import { getVerificationURL } from '../utils/qrGenerator';
import {
  supabase,
  isSupabaseConfigured,
  fetchVerificationsFromSupabase,
} from '../utils/supabaseClient';
import {
  FiShield, FiSearch, FiCheckCircle, FiXCircle,
  FiAlertTriangle, FiClock, FiCopy, FiGlobe, FiLock,
  FiAward, FiUser, FiCalendar, FiTag, FiMail, FiZap,
  FiEye, FiBarChart2, FiWifi, FiRadio, FiSmartphone,
} from 'react-icons/fi';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } }
};

const statusConfig = {
  valid: {
    icon: FiCheckCircle,
    label: 'VERIFIED AND AUTHENTIC',
    sub: 'Credential cryptographically verified — Chain of Trust intact',
    border: 'border-emerald-400',
    iconColor: 'text-emerald-500',
    badgeBg: 'bg-emerald-50',
    badgeText: 'text-emerald-700',
    badgeBorder: 'border-emerald-200',
    gradientFrom: 'from-emerald-50',
    headerBg: 'bg-emerald-500',
  },
  revoked: {
    icon: FiXCircle,
    label: 'CREDENTIAL REVOKED',
    sub: 'This credential has been officially revoked by the issuing authority',
    border: 'border-red-400',
    iconColor: 'text-red-500',
    badgeBg: 'bg-red-50',
    badgeText: 'text-red-700',
    badgeBorder: 'border-red-200',
    gradientFrom: 'from-red-50',
    headerBg: 'bg-red-500',
  },
  expired: {
    icon: FiClock,
    label: 'CREDENTIAL EXPIRED',
    sub: 'This credential has passed its validity window',
    border: 'border-amber-400',
    iconColor: 'text-amber-500',
    badgeBg: 'bg-amber-50',
    badgeText: 'text-amber-700',
    badgeBorder: 'border-amber-200',
    gradientFrom: 'from-amber-50',
    headerBg: 'bg-amber-500',
  },
  not_generated: {
    icon: FiAlertTriangle,
    label: 'DOCUMENT NOT ISSUED',
    sub: 'This record exists but no document has been generated yet',
    border: 'border-slate-400',
    iconColor: 'text-slate-500',
    badgeBg: 'bg-slate-50',
    badgeText: 'text-slate-700',
    badgeBorder: 'border-slate-200',
    gradientFrom: 'from-slate-50',
    headerBg: 'bg-slate-500',
  },
  not_found: {
    icon: FiXCircle,
    label: 'CREDENTIAL NOT FOUND',
    sub: 'No credential matches this ID in the Innovation Hacks registry',
    border: 'border-rose-400',
    iconColor: 'text-rose-500',
    badgeBg: 'bg-rose-50',
    badgeText: 'text-rose-700',
    badgeBorder: 'border-rose-200',
    gradientFrom: 'from-rose-50',
    headerBg: 'bg-rose-500',
  },
};

/** Normalise a raw Supabase verifications row into the display shape. */
function normaliseRow(r) {
  return {
    id:         r.id,
    certId:     r.document_id,
    verifiedId: r.verified_id,
    at:         r.verified_at,
    status:     r.status || 'valid',
    verifiedBy: r.verified_by || 'Public QR Scanner',
    userAgent:  r.user_agent  || '',
    source:     'supabase',
    isNew:      false,
  };
}

/** Detect the likely device type from a user-agent string. */
function deviceLabel(ua = '') {
  if (!ua) return '';
  const u = ua.toLowerCase();
  if (u.includes('iphone') || u.includes('android') || u.includes('mobile')) return '📱 Mobile';
  if (u.includes('ipad') || u.includes('tablet')) return '📟 Tablet';
  if (u.includes('mac')) return '🍎 Mac';
  if (u.includes('windows')) return '💻 Windows';
  if (u.includes('linux')) return '🐧 Linux';
  return '🌐 Browser';
}

export default function Verification() {
  const { candidates, verificationLog, logVerification } = usePlatform();
  const [search, setSearch]       = useState('');
  const [result, setResult]       = useState(null);
  const [loading, setLoading]     = useState(false);
  const [searched, setSearched]   = useState(false);

  // Supabase-backed scan records
  const [supabaseVerifs, setSupabaseVerifs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [realtimeReady, setRealtimeReady]   = useState(false);
  const [newScanIds, setNewScanIds]         = useState(new Set()); // IDs to flash
  const channelRef = useRef(null);

  const allCerts = [...candidates];
  const now      = new Date();

  // ── Load historical scans from Supabase ─────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setHistoryLoading(true);
      const rows = await fetchVerificationsFromSupabase();
      if (!cancelled) {
        setSupabaseVerifs(rows.map(normaliseRow));
        setHistoryLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  // ── Supabase Realtime subscription ──────────────────────────────────────
  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) return;

    const channel = supabase
      .channel('verifications-admin-live', {
        config: { broadcast: { self: false } },
      })
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'verifications' },
        (payload) => {
          const row = payload.new;
          const normalised = { ...normaliseRow(row), isNew: true };
          setSupabaseVerifs(prev => [normalised, ...prev]);
          // Flash the new row for 4 s then clear the highlight
          setNewScanIds(prev => new Set([...prev, row.id]));
          setTimeout(() => {
            setNewScanIds(prev => {
              const next = new Set(prev);
              next.delete(row.id);
              return next;
            });
          }, 4000);
          toast.success(`🔔 QR Scan — ${row.document_id || 'credential'}`, {
            duration: 3500,
            icon: '📡',
          });
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') setRealtimeReady(true);
      });

    channelRef.current = channel;
    return () => {
      supabase.removeChannel(channel);
      setRealtimeReady(false);
    };
  }, []);

  // ── Merge Supabase history + localStorage fallback ───────────────────────
  // De-duplicate: if a certId+timestamp combo appears in both, prefer Supabase row.
  const mergedLog = React.useMemo(() => {
    const sbIds = new Set(supabaseVerifs.map(v => v.certId + '|' + v.at.substring(0, 16)));
    const localFiltered = verificationLog.filter(v => {
      const key = (v.certId || v.verifiedId) + '|' + (v.at || '').substring(0, 16);
      return !sbIds.has(key);
    }).map(v => ({ ...v, source: 'local' }));

    return [...supabaseVerifs, ...localFiltered]
      .sort((a, b) => new Date(b.at) - new Date(a.at));
  }, [supabaseVerifs, verificationLog]);

  // ── Stats ────────────────────────────────────────────────────────────────
  const totalVerifications = mergedLog.length;
  const todayVerifs  = mergedLog.filter(v => new Date(v.at).toDateString() === now.toDateString()).length;
  const validVerifs  = mergedLog.filter(v => v.status === 'valid').length;
  const successRate  = totalVerifications > 0
    ? Math.round((validVerifs / totalVerifications) * 100)
    : 100;

  // ── Admin verify handler (search bar) ────────────────────────────────────
  const getVerifStatus = (cert) => {
    if (!cert) return 'not_found';
    if (cert.certStatus === 'revoked') return 'revoked';
    if (cert.expiryDate && new Date(cert.expiryDate) < now) return 'expired';
    if (cert.docStatus !== 'generated') return 'not_generated';
    return 'valid';
  };

  const handleVerify = async (e) => {
    if (e) e.preventDefault();
    const id = search.trim();
    if (!id) return toast.error('Please enter a Credential ID');
    setLoading(true);
    setSearched(false);
    await new Promise(r => setTimeout(r, 800));
    const cert = allCerts.find(c => c.generatedId === id || c.verificationId === id || c.id === id);
    const status = getVerifStatus(cert);
    if (cert) {
      logVerification({
        id: crypto.randomUUID(),
        certId: cert.generatedId,
        verifiedId: id,
        at: new Date().toISOString(),
        status,
        verifiedBy: 'Innovation Hacks Admin Center'
      });
    }
    setResult({ cert, status });
    setSearched(true);
    setLoading(false);
  };

  const cfg = result ? statusConfig[result.status] : null;

  return (
    <div className="space-y-8 pb-16">

      {/* ── Dark Hero Header ─────────────────────────────────────────────── */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border border-slate-800 shadow-2xl p-10">
        {/* Ambient glows */}
        <div className="absolute -top-20 left-1/3 w-80 h-80 bg-blue-600/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 right-1/4 w-60 h-60 bg-violet-600/10 rounded-full blur-3xl pointer-events-none" />
        {/* Watermark icon */}
        <div className="absolute top-6 right-8 opacity-[0.05] pointer-events-none select-none">
          <FiShield style={{ width: 180, height: 180, color: 'white' }} />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-2xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
              <FiShield className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <div className="text-blue-400 font-black text-[10px] uppercase tracking-[0.25em]">Innovation Hacks</div>
              <div className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">Cryptographic Trust Layer v2</div>
            </div>

            {/* Live indicator */}
            <div className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-800/80 border border-slate-700/60">
              <span className={`w-2 h-2 rounded-full ${realtimeReady ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'}`} />
              <span className={`text-[10px] font-black uppercase tracking-wider ${realtimeReady ? 'text-emerald-400' : 'text-slate-500'}`}>
                {realtimeReady ? 'Live Feed Active' : isSupabaseConfigured ? 'Connecting…' : 'Offline Mode'}
              </span>
              {realtimeReady && <FiRadio className="w-3 h-3 text-emerald-400" />}
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-black text-white mb-3 leading-tight">
            Credential Verification
            <span className="text-blue-400"> Center</span>
          </h1>
          <p className="text-slate-400 text-sm font-semibold max-w-xl leading-relaxed">
            Instantly authenticate any Innovation Hacks internship credential. Every certificate is cryptographically signed with a unique QR-verified chain of trust.
          </p>

          {/* Stats */}
          <div className="flex flex-wrap items-center gap-6 mt-8 pt-6 border-t border-slate-800/80">
            {[
              { icon: FiEye,         val: historyLoading ? '…' : totalVerifications, label: 'Total Checks' },
              { icon: FiZap,         val: historyLoading ? '…' : todayVerifs,        label: 'Today' },
              { icon: FiCheckCircle, val: historyLoading ? '…' : validVerifs,        label: 'Valid' },
              { icon: FiBarChart2,   val: historyLoading ? '…' : successRate + '%',  label: 'Success Rate' },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700/60 flex items-center justify-center">
                  <s.icon className="w-4 h-4 text-blue-400" />
                </div>
                <div>
                  <div className="text-white font-black text-2xl leading-none">{s.val}</div>
                  <div className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-0.5">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ── Search Form ─────────────────────────────────────────────────── */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.4 }}>
        <form onSubmit={handleVerify}>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <FiSearch className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none z-10" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Enter Credential ID — e.g. INT-2026-0008"
                className="w-full h-14 pl-14 pr-6 bg-white border-2 border-slate-200 rounded-2xl text-slate-900 font-bold placeholder:text-slate-400 placeholder:font-normal focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all text-sm shadow-sm"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="h-14 px-8 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-60 text-white font-black rounded-2xl transition-all shadow-lg shadow-blue-600/25 flex items-center gap-2.5 whitespace-nowrap text-sm"
            >
              {loading
                ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Verifying...</>
                : <><FiShield className="w-4 h-4" /> Verify Credential</>
              }
            </button>
          </div>
        </form>
      </motion.div>

      {/* ── Results ─────────────────────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        {loading && (
          <motion.div key="loading"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center py-20 gap-6">
            <div className="relative w-24 h-24">
              <div className="absolute inset-0 rounded-full border-4 border-blue-100 animate-pulse" />
              <div className="absolute inset-3 rounded-full border-4 border-t-blue-600 border-r-transparent border-b-transparent border-l-transparent animate-spin" />
              <div className="absolute inset-0 flex items-center justify-center">
                <FiShield className="w-9 h-9 text-blue-500" />
              </div>
            </div>
            <div className="text-center">
              <p className="font-black text-slate-900 text-lg">Scanning Innovation Hacks Registry...</p>
              <p className="text-slate-500 text-sm font-semibold mt-1">Cryptographic verification in progress</p>
            </div>
          </motion.div>
        )}

        {searched && result && cfg && !loading && (
          <motion.div key="result"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className={`relative overflow-hidden rounded-3xl border-2 ${cfg.border} shadow-xl bg-white`}>

            {/* Gradient strip at top */}
            <div className={`h-2 w-full ${cfg.headerBg}`} />

            <div className="p-8">
              {/* Status row */}
              <div className="flex items-start justify-between gap-6 mb-8">
                <div className="flex items-center gap-5">
                  <div className={`w-16 h-16 rounded-2xl bg-white border-2 ${cfg.border} flex items-center justify-center shadow-md`}>
                    <cfg.icon className={`w-8 h-8 ${cfg.iconColor}`} />
                  </div>
                  <div>
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-[0.12em] mb-2 ${cfg.badgeBg} ${cfg.badgeText} ${cfg.badgeBorder}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse inline-block" />
                      {cfg.label}
                    </span>
                    <p className="text-slate-700 font-semibold text-sm max-w-md">{cfg.sub}</p>
                  </div>
                </div>

                <div className="text-right flex-shrink-0 bg-slate-50 border-2 border-slate-200 rounded-2xl p-4">
                  <div className="text-[10px] text-slate-500 font-black uppercase tracking-widest mb-1">Verified At</div>
                  <div className="font-black text-slate-900 text-base">{new Date().toLocaleTimeString()}</div>
                  <div className="text-xs text-slate-500 font-semibold">{new Date().toLocaleDateString()}</div>
                </div>
              </div>

              {/* Credential info grid */}
              {result.cert && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    {[
                      { icon: FiUser,     label: 'Candidate Name', val: result.cert.name || 'N/A' },
                      { icon: FiTag,      label: 'Credential ID',  val: result.cert.generatedId || 'N/A', mono: true },
                      { icon: FiMail,     label: 'Email',          val: result.cert.email || 'N/A', mono: true },
                      { icon: FiAward,    label: 'Role',           val: result.cert.role || result.cert.domain || 'N/A' },
                      { icon: FiCalendar, label: 'Duration',       val: result.cert.duration || (result.cert.raw && result.cert.raw.Duration) || 'N/A' },
                      { icon: FiGlobe,    label: 'Institution',    val: result.cert.college || 'Innovation Hacks' },
                    ].map(f => (
                      <div key={f.label} className="bg-slate-50 border-2 border-slate-200 rounded-2xl p-4 space-y-1.5 hover:border-slate-300 transition-all">
                        <div className="flex items-center gap-1.5 text-slate-500">
                          <f.icon className="w-3.5 h-3.5" />
                          <span className="text-[10px] font-black uppercase tracking-widest">{f.label}</span>
                        </div>
                        <div className={`font-bold text-slate-900 text-sm leading-snug ${f.mono ? 'font-mono' : ''}`}>{f.val}</div>
                      </div>
                    ))}
                  </div>

                  {/* Verification URL */}
                  <div className="flex items-center gap-3 p-4 bg-slate-50 border-2 border-slate-200 rounded-2xl">
                    <FiLock className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    <code className="text-xs text-slate-700 font-mono flex-1 truncate">
                      {getVerificationURL(result.cert.generatedId || result.cert.verificationId)}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(getVerificationURL(result.cert.generatedId || result.cert.verificationId));
                        toast.success('Verification URL copied!');
                      }}
                      className="flex items-center gap-1.5 px-4 py-2 bg-white border-2 border-slate-200 rounded-xl text-xs font-black text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all flex-shrink-0"
                    >
                      <FiCopy className="w-3.5 h-3.5" /> Copy URL
                    </button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Live Scan Feed / Audit Trail ────────────────────────────────── */}
      {(mergedLog.length > 0 || historyLoading) && (
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.4 }}
          className="bg-white border-2 border-slate-200 rounded-3xl overflow-hidden shadow-sm">

          {/* Header */}
          <div className="px-8 py-5 border-b-2 border-slate-200 flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-slate-950 flex items-center justify-center flex-shrink-0">
              <FiBarChart2 className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="font-black text-slate-950 text-base flex items-center gap-2">
                Verification Audit Trail
                {realtimeReady && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-[9px] font-black uppercase tracking-wider">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
                    Live
                  </span>
                )}
              </h2>
              <p className="text-slate-500 text-xs font-semibold">
                {isSupabaseConfigured
                  ? 'Real-time QR scan events from all devices — secured by Supabase RLS'
                  : 'Local session scan log (connect Supabase for worldwide tracking)'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {historyLoading && (
                <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
              )}
              <div className="bg-slate-100 border-2 border-slate-200 text-slate-700 font-black text-xs px-4 py-2 rounded-xl">
                {mergedLog.length} records
              </div>
            </div>
          </div>

          {/* Rows */}
          <div className="divide-y-2 divide-slate-100">
            {mergedLog.slice(0, 50).map((v, i) => {
              const s = statusConfig[v.status] || statusConfig.not_found;
              const StatusIcon = s.icon;
              const isHighlighted = newScanIds.has(v.id);
              const device = deviceLabel(v.userAgent);

              return (
                <motion.div key={v.id}
                  initial={v.isNew ? { opacity: 0, x: -16, backgroundColor: '#ecfdf5' } : { opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0, backgroundColor: isHighlighted ? '#ecfdf5' : '#ffffff' }}
                  transition={{ delay: v.isNew ? 0 : i * 0.025, duration: 0.35 }}
                  className="flex items-center gap-5 px-8 py-4 hover:bg-slate-50 transition-all relative">

                  {/* New-scan pulse line on the left */}
                  {isHighlighted && (
                    <span className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-400 rounded-full" />
                  )}

                  <div className={`w-10 h-10 rounded-xl border-2 ${s.border} flex items-center justify-center flex-shrink-0 bg-white`}>
                    <StatusIcon className={`w-5 h-5 ${s.iconColor}`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-black text-slate-950 text-sm font-mono">
                        {v.certId || v.verifiedId}
                      </span>
                      {isHighlighted && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-black uppercase tracking-wider animate-pulse">
                          ✦ New Scan
                        </span>
                      )}
                      {v.source === 'supabase' && !isHighlighted && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-600 text-[9px] font-black uppercase tracking-wider">
                          <FiWifi className="w-2.5 h-2.5" /> QR Scan
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      <span className="text-slate-500 text-xs font-semibold">
                        {new Date(v.at).toLocaleString()}
                      </span>
                      {device && (
                        <span className="text-slate-400 text-[10px] font-bold">{device}</span>
                      )}
                      {v.verifiedBy && (
                        <span className="text-slate-400 text-[10px] font-semibold">{v.verifiedBy}</span>
                      )}
                    </div>
                  </div>

                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-[10px] font-black uppercase tracking-wider flex-shrink-0 ${s.badgeBg} ${s.badgeText} ${s.badgeBorder}`}>
                    {v.status.replace(/_/g, ' ')}
                  </span>
                </motion.div>
              );
            })}
          </div>

          {/* Footer note */}
          {mergedLog.length > 50 && (
            <div className="px-8 py-4 border-t-2 border-slate-100 text-center text-xs text-slate-500 font-semibold">
              Showing 50 of {mergedLog.length} total scan records
            </div>
          )}
        </motion.div>
      )}

      {/* ── Empty State ──────────────────────────────────────────────────── */}
      {!searched && !loading && mergedLog.length === 0 && !historyLoading && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="flex flex-col items-center justify-center py-24 gap-5">
          <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-slate-100 to-slate-200 border-2 border-slate-300 flex items-center justify-center">
            <FiShield className="w-12 h-12 text-slate-400" />
          </div>
          <div className="text-center max-w-sm">
            <p className="font-black text-slate-950 text-xl mb-2">Ready to Verify</p>
            <p className="text-slate-500 text-sm font-semibold leading-relaxed">
              Enter any Innovation Hacks Credential ID above to instantly verify its authenticity.
              {isSupabaseConfigured
                ? ' QR scans from any device will appear here live.'
                : ' Connect Supabase to enable worldwide live scan tracking.'}
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
}
